import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../axios';
import { Card, PageHeader, Button, InfoRow } from '../UI';
import classes from './DeviceDetail.module.css';
import { downloadDevices } from '../../utils/downloadDevices';

import { ReactComponent as LinkIcon } from '../../assets/icons/Link.svg';
import { ReactComponent as BatteryIcon } from '../../assets/icons/Battery.svg';
import { ReactComponent as PowerIcon } from '../../assets/icons/Power.svg';
import { runtimeConfig } from '../../config/runtime';

const DeviceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [projects, setProjects] = useState({ success: false, projects: [] });
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    loadDevice();
    loadProjects();
  }, [id]);

  const loadDevice = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/devices/${id}`);
      if (response.data && response.data.device) {
        const loadedDevice = response.data.device;
        setDevice(loadedDevice);
        setSelectedProjectId(loadedDevice?.project?.id ? String(loadedDevice.project.id) : "");
      } else {
        navigate('/devices');
      }
    } catch (error) {
      console.error('Failed to load device:', error);
      navigate('/devices');
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await axios.get('/projects');
      setProjects(response.data)
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  }

  const handleChangeAssigned = async (projectId) => {
    const nextProjectId = String(projectId || "");
    setSelectedProjectId(nextProjectId);
    try {
      const res = nextProjectId
        ? await axios.put(`/devices/${id}/reassign-project`, { projectId: nextProjectId })
        : await axios.put(`/devices/${id}/unassign-project`);

      const nextProject = res?.data?.device?.project ?? null;
      setDevice((prev) => prev ? ({ ...prev, project: nextProject }) : prev);
    } catch (error) {
      console.error(error)
    }
  }

  const isAssignedToProject = (projectId) => {
    const assignedId = device?.project?.id;
    if (!assignedId) return false;
    return String(assignedId) === String(projectId);
  }

  const handleSaveNote = async () => {
    setSavingNote(true);
    try {
      // TODO: Implement API call to save note
      alert('Note saved successfully');
    } catch (error) {
      console.error('Failed to save note:', error);
      alert('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDownloadCertificate = () => {
    downloadDevices([device.id], device.projectId, device.projectName);
  };

  if (loading) {
    return (
      <div className={classes.deviceDetail}>
        <div className={classes.loading}>Loading device details...</div>
      </div>
    );
  }

  if (!device) {
    return (
      <div className={classes.deviceDetail}>
        <div className={classes.error}>Device not found</div>
      </div>
    );
  }

  const DeviceLabel = ({ text, Icon, link }) => {
    if (link) {
      return (
        <a className={classes.label} href={link} target="_blank" rel="noopener noreferrer">
          <p>{text}</p>
          <Icon className={classes.labelIcon} aria-hidden="true" />
        </a>
      )
    }
    return (
      <div className={classes.label}>
        <p>{text}</p>
        <Icon style={{ width: 20, height: 20 }} aria-hidden="true" />
      </div>
    )
  }

  return (
    <div className={classes.deviceDetail}>
      <PageHeader 
        title={device.serialNumber}
        subtitle={device.deviceModel}
        lastPage={true}
        titleTag="h1"
      />

      <div className={classes.content}>
        <div className={classes.infoSection}>
          <Card>
            <InfoRow label="Serial number" value={device.serialNumber} />
            <InfoRow label="Device EUI" value={device.deviceEUI} />
            <InfoRow label="Date added" value={device.dateAdded} />
            <InfoRow label="Manufacturer" value={device.manufacturer} />
            <InfoRow label="Model" value={device.model} />
            <div className={classes.projectSelect}>
              <p>Project</p>
              <select 
                name="project"
                value={selectedProjectId}
                onChange={(e) => handleChangeAssigned(e.target.value)}
              >
                {(() => {
                  const list = projects?.success ? (projects.projects || []) : [];
                  const getPid = (p) => String(p?._id ?? p?.id ?? "");

                  const selected =
                    selectedProjectId
                      ? list.find((p) => getPid(p) === String(selectedProjectId))
                      : null;

                  const others = list.filter((p) => getPid(p) && getPid(p) !== String(selectedProjectId));

                  const ordered = selected ? [selected, ...others] : others;

                  return (
                    <>
                      {!selectedProjectId && (
                        <option key="unassigned" value="">
                          :
                        </option>
                      )}

                      {ordered.map((project) => {
                        const pid = getPid(project);
                        if (!pid) return null;
                        return (
                          <option key={pid} value={pid}>
                            {project.name}
                          </option>
                        );
                      })}

                      {!!selectedProjectId && (
                        <option key="unassigned" value="">
                          :
                        </option>
                      )}
                    </>
                  );
                })()}
              </select>
            </div>

            <label className={classes.noteLabel}>Notes</label>
            <textarea
              className={classes.noteTextarea}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add your notes about this device..."
              rows={6}
            />
            <div className={classes.buttonsRow}>
              <Button
                variant="secondary"
                onClick={handleSaveNote}
                disabled={savingNote}
              >
                {savingNote ? 'Saving...' : 'Save Note'}
              </Button>
              <Button
                variant="primary"
                onClick={handleDownloadCertificate}
                fullWidth
              >
                Download certificate
              </Button>
            </div>
          </Card>
        </div>

        <div className={classes.deviceImageSection}>
          <Card>
            <div className={classes.deviceImageContainer} style={{ padding: device.imagePadding, backgroundColor: device.imageBackground }}>
              <img
                src={runtimeConfig.apiBaseUrl + device.imageUrl}
                alt={device.deviceModel}
                className={classes.deviceImage}
                style={{ width: device.imageWidth, maxHeight: device.imageMaxHeight }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className={classes.deviceImagePlaceholder} style={{ display: 'none' }}>
                Device Image
              </div>
            </div>
            <div className={classes.deviceInfo}>
              <h3 className={classes.deviceInfoTitle}>{device.productName}</h3>
              <p className={classes.deviceInfoManufacturer}>{device.productVendor}</p>
              <p className={classes.deviceInfoTagline}>{device.tagline}</p>
              <div className={classes.labelsRow}>
                <DeviceLabel
                  text={device.sleepyDevice ? "Sleepy device" : "Powered device"}
                  Icon={device.sleepyDevice ? BatteryIcon : PowerIcon}
                />
                <DeviceLabel text="Product page" Icon={LinkIcon} link={device.productPageLink} />
                <DeviceLabel text="Documentation" Icon={LinkIcon} link={device.documentationLink} />
              </div>
              {
                device.description && 
                device.description.map((item, index) => (
                  <p className={classes.deviceInfoDescription} key={index}>{item}</p>
                ))
              }
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DeviceDetail;

